import { Plus, Trash2, DollarSign, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { formatCurrency } from "@/lib/constants";
import { useTheme } from "@/contexts/ThemeContext";

export default function HourlyEntries({ entries, onAdd, onRemove, onUpdate }) {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const totalPay = entries.reduce((sum, e) => sum + (parseFloat(e.rate) || 0) * (parseFloat(e.hours) || 0), 0);

  return (
    <div data-testid="hourly-pay-section" className={`rounded-xl border-2 p-6 mb-8 ${
      isDark 
        ? 'border-cyan-500/20 bg-cyan-500/5'
        : 'border-cyan-200 bg-cyan-50/30'
    }`}>
      <div className="flex items-center justify-between mb-5">
        <div>
          <h3 className={`text-2xl md:text-3xl font-bold ${
            isDark ? 'text-cyan-300' : 'text-cyan-700'
          }`} style={{ fontFamily: "'Manrope', sans-serif" }}>
            Hourly Pay
          </h3>
          <p className={`text-sm mt-1 ${isDark ? 'text-slate-500' : 'text-gray-600'}`}>
            Add hourly compensation entries — one per rate
          </p>
          <p className={`text-xs italic ${isDark ? 'text-slate-600' : 'text-gray-500'}`}>
            Does not count toward patient total
          </p>
        </div>
        <Button
          data-testid="add-hourly-entry-btn"
          onClick={onAdd}
          className={`border transition-colors h-12 px-5 text-base font-semibold ${
            isDark 
              ? 'bg-cyan-600/30 hover:bg-cyan-600/50 text-cyan-300 border-cyan-500/30 hover:border-cyan-400/50'
              : 'bg-cyan-100 hover:bg-cyan-200 text-cyan-700 border-cyan-300 hover:border-cyan-500'
          }`}
        >
          <Plus size={20} className="mr-2" />
          Add Entry
        </Button>
      </div>

      {entries.length === 0 && (
        <p data-testid="hourly-empty" className={`text-base italic py-6 text-center ${
          isDark ? 'text-slate-600' : 'text-gray-500'
        }`}>
          No hourly pay entries yet — click "Add Entry" to log one
        </p>
      )}

      <div className="space-y-4">
        {entries.map((entry, index) => {
          const entryTotal = (parseFloat(entry.rate) || 0) * (parseFloat(entry.hours) || 0);
          return (
            <div key={entry.id}>
              {/* Mobile layout - stacked */}
              <div
                data-testid={`hourly-entry-${index}`}
                className="sm:hidden space-y-3 animate-slide-up"
                style={{ animationDelay: '0s' }}
              >
                {/* Description - full width on mobile */}
                <Input
                  data-testid={`hourly-label-${index}`}
                  placeholder="Description (optional)..."
                  value={entry.label}
                  onChange={(e) => onUpdate(entry.id, "label", e.target.value)}
                  className={`border text-base h-12 w-full ${
                    isDark 
                      ? 'bg-slate-800/50 border-slate-700/50 hover:border-slate-600 focus:border-cyan-500/50 text-slate-200 placeholder:text-slate-600'
                      : 'bg-gray-50 border-gray-300 hover:border-gray-400 focus:border-cyan-500 text-gray-900 placeholder:text-gray-400'
                  }`}
                />
                {/* Rate, Hours, Total, Delete - in a row */}
                <div className="flex items-center gap-2">
                  <div className="w-24">
                    <div className="relative">
                      <DollarSign className={`absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 ${
                        isDark ? 'text-cyan-400/60' : 'text-cyan-600/60'
                      }`} />
                      <Input
                        data-testid={`hourly-rate-mobile-${index}`}
                        type="number"
                        step="0.01"
                        min="0"
                        placeholder="$/hr"
                        value={entry.rate || ""}
                        onChange={(e) => onUpdate(entry.id, "rate", e.target.value)}
                        className={`pl-6 pr-6 border font-mono text-sm h-10 ${
                          isDark 
                            ? 'bg-slate-800/50 border-slate-700/50 hover:border-slate-600 focus:border-cyan-500/50 text-cyan-300 placeholder:text-slate-600'
                            : 'bg-gray-50 border-gray-300 hover:border-gray-400 focus:border-cyan-500 text-cyan-700 placeholder:text-gray-400'
                        }`}
                      />
                      <span className="absolute right-1.5 top-1/2 -translate-y-1/2 text-[9px] text-slate-500 font-mono">/h</span>
                    </div>
                  </div>
                  <div className="w-20">
                    <div className="relative">
                      <Clock className={`absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 ${
                        isDark ? 'text-cyan-400/60' : 'text-cyan-600/60'
                      }`} />
                      <Input
                        data-testid={`hourly-hours-mobile-${index}`}
                        type="number"
                        step="0.25"
                        min="0"
                        placeholder="hrs"
                        value={entry.hours || ""}
                        onChange={(e) => onUpdate(entry.id, "hours", e.target.value)}
                        className={`pl-6 pr-6 border font-mono text-sm h-10 ${
                          isDark 
                            ? 'bg-slate-800/50 border-slate-700/50 hover:border-slate-600 focus:border-cyan-500/50 text-cyan-300 placeholder:text-slate-600'
                            : 'bg-gray-50 border-gray-300 hover:border-gray-400 focus:border-cyan-500 text-cyan-700 placeholder:text-gray-400'
                        }`}
                      />
                      <span className="absolute right-1 top-1/2 -translate-y-1/2 text-[9px] text-slate-500 font-mono">hr</span>
                    </div>
                  </div>
                  <div className="flex-1 text-right">
                    <span data-testid={`hourly-subtotal-mobile-${index}`} className={`text-sm font-mono font-bold ${
                      isDark ? 'text-cyan-300' : 'text-cyan-600'
                    }`}>{formatCurrency(entryTotal)}</span>
                  </div>
                  <Button
                    data-testid={`hourly-delete-mobile-${index}`}
                    size="icon"
                    variant="ghost"
                    onClick={() => onRemove(entry.id)}
                    className={`h-10 w-10 flex-shrink-0 ${
                      isDark 
                        ? 'text-slate-600 hover:text-red-400 hover:bg-red-500/10'
                        : 'text-gray-400 hover:text-red-500 hover:bg-red-50'
                    }`}
                  >
                    <Trash2 size={16} />
                  </Button>
                </div>
              </div>
              
              {/* Desktop layout - horizontal */}
              <div
                className="hidden sm:flex items-center gap-4 animate-slide-up"
                style={{ animationDelay: '0s' }}
              >
                <div className="flex-1">
                  <Input
                    data-testid={`hourly-label-${index}`}
                    placeholder="Description (optional)..."
                    value={entry.label}
                    onChange={(e) => onUpdate(entry.id, "label", e.target.value)}
                    className={`border text-base h-12 ${
                      isDark 
                        ? 'bg-slate-800/50 border-slate-700/50 hover:border-slate-600 focus:border-cyan-500/50 text-slate-200 placeholder:text-slate-600'
                        : 'bg-gray-50 border-gray-300 hover:border-gray-400 focus:border-cyan-500 text-gray-900 placeholder:text-gray-400'
                    }`}
                  />
                </div>
                <div className="w-36">
                  <div className="relative">
                    <DollarSign className={`absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 ${
                      isDark ? 'text-cyan-400/60' : 'text-cyan-600/60'
                    }`} />
                    <Input
                      data-testid={`hourly-rate-${index}`}
                      type="number"
                      step="0.01"
                      min="0"
                      placeholder="$/hr"
                      value={entry.rate || ""}
                      onChange={(e) => onUpdate(entry.id, "rate", e.target.value)}
                      className={`pl-8 border font-mono text-base h-12 pr-10 ${
                        isDark 
                          ? 'bg-slate-800/50 border-slate-700/50 hover:border-slate-600 focus:border-cyan-500/50 text-cyan-300 placeholder:text-slate-600'
                          : 'bg-gray-50 border-gray-300 hover:border-gray-400 focus:border-cyan-500 text-cyan-700 placeholder:text-gray-400'
                      }`}
                    />
                    <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[10px] text-slate-500 font-mono">/hr</span>
                  </div>
                </div>
                <div className="w-32">
                  <div className="relative">
                    <Clock className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-cyan-400/60" />
                    <Input
                      data-testid={`hourly-hours-${index}`}
                      type="number"
                      step="0.25"
                      min="0"
                      placeholder="hrs"
                      value={entry.hours || ""}
                      onChange={(e) => onUpdate(entry.id, "hours", e.target.value)}
                      className={`pl-8 border font-mono text-base h-12 pr-10 ${
                        isDark 
                          ? 'bg-slate-800/50 border-slate-700/50 hover:border-slate-600 focus:border-cyan-500/50 text-cyan-300 placeholder:text-slate-600'
                          : 'bg-gray-50 border-gray-300 hover:border-gray-400 focus:border-cyan-500 text-cyan-700 placeholder:text-gray-400'
                      }`}
                    />
                    <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[10px] text-slate-500 font-mono">hrs</span>
                  </div>
                </div>
                <div className="w-28 text-right">
                  <span data-testid={`hourly-subtotal-${index}`} className={`text-sm font-mono font-bold ${
                    isDark ? 'text-cyan-300' : 'text-cyan-600'
                  }`}>{formatCurrency(entryTotal)}</span>
                </div>
                <Button
                  data-testid={`hourly-delete-${index}`}
                  size="icon"
                  variant="ghost"
                  onClick={() => onRemove(entry.id)}
                  className={`h-12 w-12 flex-shrink-0 ${
                    isDark 
                      ? 'text-slate-600 hover:text-red-400 hover:bg-red-500/10'
                      : 'text-gray-400 hover:text-red-500 hover:bg-red-50'
                  }`}
                >
                  <Trash2 size={18} />
                </Button>
              </div>
            </div>
          );
        })}
      </div>

      {entries.length > 0 && (
        <div className="mt-5 pt-4 border-t border-cyan-500/10 flex items-center justify-between">
          <span className="text-sm text-slate-500">{entries.length} hourly entr{entries.length !== 1 ? 'ies' : 'y'}</span>
          <span data-testid="hourly-total" className="text-base font-mono font-bold text-cyan-300">
            {formatCurrency(totalPay)}
          </span>
        </div>
      )}
    </div>
  );
}
